import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

export const RequireAdmin = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-midnight">
        <div className="font-mono text-xs tracking-widest uppercase text-white/40">
          Loading...
        </div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/admin/login" replace state={{ from: location }} />;
  }
  return children;
};

export const PageHero = ({ overline, title, desc, image, children }) => (
  <section className="relative overflow-hidden bg-midnight">
    {image && (
      <div className="absolute inset-0">
        <img src={image} alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-midnight via-midnight/70 to-midnight/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-midnight/40 via-transparent to-midnight" />
      </div>
    )}
    <div className="absolute inset-0 tech-grid-bg" />
    <div className="absolute -top-20 -right-32 w-[420px] h-[420px] rounded-full bg-coral/10 blur-3xl" />
    <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pt-40 md:pt-48 pb-20 md:pb-28">
      <div className="overline mb-6">{overline}</div>
      <h1 className="font-display font-black tracking-tighter leading-[0.95] text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl max-w-5xl uppercase">
        {title}
      </h1>
      {desc && (
        <p className="mt-8 max-w-2xl text-white/65 text-lg md:text-xl font-display font-light leading-relaxed">
          {desc}
        </p>
      )}
      {children}
    </div>
  </section>
);

export const SectionTitle = ({ overline, title, desc, align = "left" }) => (
  <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
    {overline && <div className="overline mb-5">{overline}</div>}
    <h2 className="font-display font-extrabold tracking-tighter text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.05] uppercase">
      {title}
    </h2>
    {desc && (
      <p className="mt-6 text-white/65 text-base md:text-lg leading-relaxed">{desc}</p>
    )}
  </div>
);

export const FeatureCard = ({ title, desc, index }) => (
  <div
    data-testid={`feature-${index}`}
    className="group relative bg-midnight-2 border border-white/5 p-8 lg:p-10 h-full flex flex-col transition-all duration-300 hover:border-coral hover:-translate-y-1 hover:bg-midnight-3"
  >
    <div className="font-mono text-[11px] tracking-widest text-coral mb-4">
      /{String(index).padStart(2, "0")}
    </div>
    <h3 className="font-display font-bold text-xl md:text-2xl text-white mb-3 leading-tight">
      {title}
    </h3>
    <p className="text-white/55 text-sm leading-relaxed">{desc}</p>
    <div className="mt-auto pt-8 flex items-center gap-3 text-white/40 group-hover:text-coral transition-colors">
      <span className="line-arrow" />
    </div>
  </div>
);

export const CtaBlock = ({ title, desc, buttonText, to = "/contacts" }) => (
  <section className="relative bg-midnight-2 text-white overflow-hidden border-t border-white/5">
    <div className="absolute inset-0 tech-grid-bg opacity-50" />
    <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-coral/15 blur-3xl" />
    <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
      <div className="md:col-span-8">
        <div className="overline mb-6">CTA · CONTACT</div>
        <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95] uppercase">
          {title}
        </h2>
        {desc && <p className="mt-6 text-white/65 max-w-2xl text-lg">{desc}</p>}
      </div>
      <div className="md:col-span-4 flex md:justify-end">
        <Link
          to={to}
          data-testid="cta-block-button"
          className="btn-primary !py-5 !px-8"
        >
          {buttonText}
        </Link>
      </div>
    </div>
  </section>
);
